<template>
  <div class="c-base-date-picker">
    <date-picker ref="date-picker" mode="date" :value="localDate" @input="onDateUpdate" v-bind="attributes" :model-config="modelConfig" color="secondary" is-expanded />

    <div v-if="canPickTime" class="flex">
      <base-time-picker class="mr-4" :value="isDateRange ? parts.start : parts" @input="onTimeUpdate('start', $event)" :date="isDateRange ? getDateFromParts(parts.start) : getDateFromParts(parts)" :can-pick-seconds="canPickSeconds" />
      <base-time-picker v-if="isDateRange" :value="parts.end" @input="onTimeUpdate('end', $event)" :date="getDateFromParts(parts.end)" :can-pick-seconds="canPickSeconds"/>
    </div>
  </div>
</template>

<script>
/**
 * How to use the date picker
 *
 * As date picker:
 * <base-date-picker v-model="date" />
 *
 * As date-time picker
 * <base-date-picker v-model="date" :can-pick=time="true" />:
 *
 * As date range picker
 * <base-date-picker v-model="range" is-date-range="true" />
 *
 * Properties:
 * - value { [Object, Date] }: Selected date or date range
 * - columns { Number }: How many 'calenders' do you want to show
 * - first-day-of-week { Number }: The day the specified the first day of the week. This is a number from 1 to 7 (Sunday to Saturday, respectfully).
 * - min-date { Date }: Earliest date (month, year) that the user can navigate to
 * - max-date { Date }: Latest date (month, year) that the user can navigate to
 * - can-pick-time { Boolean }: Enable time picker
 * - can-pick-seconds { Boolean }: When time-picker is enabled, enable seconds option
 * - is-date-range { Boolean }: Enable range picker
 *
 * * Note: all options from v-calender will bind to the <date-picker> as v-bind:
 *
 * Default properties:
 * - value: this.isDateRange ? { } : null
 * - columns: 2
 * - first-day-of-week: 2 (monday)
 * - min-date: this.$moment().subtract(5, 'year').toDate()
 * - max-date: this.$moment().toDate()
 * - can-pick-time: false
 * - can-pick-seconds: false
 * - is-date-range: false
 */

import DatePicker from 'v-calendar/lib/components/date-picker.umd'

export default {
  name: 'BaseDatePicker',

  components: { 'date-picker': DatePicker },

  props: {
    value: {
      type: [Object, Date],
      default: function () {
        return this.isDateRange ? { } : null
      }
    },
    columns: {
      type: Number,
      default: function () {
        return window.innerWidth < 576 ? 1 : 2
      }
    },
    firstDayOfWeek: {
      type: Number,
      default: 2
    },
    minDate: {
      type: Date,
      default: function () {
        return this.$moment().subtract(5, 'year').toDate()
      }
    },
    maxDate: {
      type: Date,
      default: function () {
        return this.$moment().toDate()
      }
    },
    canPickTime: {
      type: Boolean,
      default: false
    },
    canPickSeconds: {
      type: Boolean,
      default: false
    },
    isDateRange: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      parts: {}
    }
  },

  mounted () {
    this.$watch(() => this.value, value => this.validate(value, (result) => {
      // TODO: Remove double $ref after update of v-calender
      this.isDateRange ? this.$refs['date-picker'].$refs.calendar.showPageRange({ from: result.startDate, to: result.endDate }) : this.$refs['date-picker'].$refs.calendar.focusDate(result)

      this.parts = this.isDateRange ? { start: this.getDateParts(result.startDate), end: this.getDateParts(result.endDate) } : this.getDateParts(result)
    }), { immediate: true })
  },

  computed: {
    localDate () {
      return this.isDateRange ? { start: this.getDateFromParts(this.parts.start), end: this.getDateFromParts(this.parts.end) } : this.getDateFromParts(this.parts)
    },

    attributes () {
      const { columns, firstDayOfWeek, minDate, maxDate, isDateRange: isRange } = this

      return {
        ...this.$attrs, columns, firstDayOfWeek, minDate, maxDate, isRange
      }
    },

    modelConfig () {
      return this.isDateRange ? { start: { timeAdjust: '00:00:00' }, end: { timeAdjust: '23:59:59' } } : { timeAdjust: '12:00:00' }
    }
  },

  methods: {
    validate (value, next) {
      if (value) {
        if (this.isDateRange) {
          next(this.$moment.isMoment(value.startDate) ? { startDate: value.startDate.toDate(), endDate: value.endDate.toDate() } : value)
        } else {
          next(this.$moment.isMoment(value) ? value.toDate() : value)
        }
      }
    },

    getDayParts (date) {
      const { year, month, day } = this.getDateParts(date)
      return { year, month, day }
    },

    getDateParts (date) {
      return date ? { year: date.getFullYear(), month: date.getMonth() + 1, day: date.getDate(), hours: date.getHours(), minutes: date.getMinutes(), seconds: date.getSeconds() } : { }
    },

    getDateFromParts (parts = {}) {
      const { year, month, day, hours = 0, minutes = 0, seconds = 0 } = parts
      return (typeof year !== 'undefined' || typeof month !== 'undefined' || typeof day !== 'undefined') ? new Date(year, month - 1, day, hours, minutes, seconds) : null
    },

    onDateUpdate (value) {
      if (this.isDateRange) {
        this.parts = {
          start: this.canPickTime ? { ...this.parts.start, ...this.getDayParts(value.start) } : this.getDateParts(value.start),
          end: this.canPickTime ? { ...this.parts.end, ...this.getDayParts(value.end) } : this.getDateParts(value.end)
        }
      } else {
        this.parts = this.canPickTime ? { ...this.parts, ...this.getDayParts(value) } : this.getDateParts(value)
      }

      this.updateValue()
    },

    onTimeUpdate (key, { hours, minutes, seconds }) {
      if (this.isDateRange) {
        this.parts = { ...this.parts, [key]: { ...this.parts[key], hours, minutes, seconds } }
      } else {
        this.parts = { ...this.parts, hours, minutes, seconds }
      }

      this.updateValue()
    },

    updateValue () {
      this.$emit('input', this.isDateRange ? { startDate: this.getDateFromParts(this.parts.start), endDate: this.getDateFromParts(this.parts.end) } : this.getDateFromParts(this.parts))
    }
  }
}
</script>
